import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Activity, Search, RefreshCw, Loader2, User, Settings, Shield,
  Calendar, Clock, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogsPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activity-logs`);
      // Ensure we always get an array
      const logsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.logs || response.data?.data || generateMockLogs());
      setLogs(Array.isArray(logsData) ? logsData : generateMockLogs());
    } catch (error) {
      setLogs(generateMockLogs());
    } finally {
      setLoading(false);
    }
  };

  const generateMockLogs = () => [
    { id: 1, type: 'auth', action: 'Admin Login', user: 'admin@iqraskills.org', ip: '192.168.1.1', timestamp: '2026-04-06 10:30:00' },
    { id: 2, type: 'student', action: 'Student Created', user: 'admin@iqraskills.org', details: 'Rahul Kumar', timestamp: '2026-04-06 10:15:00' },
    { id: 3, type: 'center', action: 'Center Approved', user: 'admin@iqraskills.org', details: 'ABC Institute', timestamp: '2026-04-06 09:45:00' },
    { id: 4, type: 'course', action: 'Course Updated', user: 'admin@iqraskills.org', details: 'DCA Course', timestamp: '2026-04-06 09:30:00' },
    { id: 5, type: 'payment', action: 'Payment Received', user: 'System', details: '₹4,000 from XYZ Center', timestamp: '2026-04-06 09:00:00' },
    { id: 6, type: 'auth', action: 'Sub-Admin Login', user: 'operations@iqraskills.org', ip: '192.168.1.5', timestamp: '2026-04-06 08:30:00' },
    { id: 7, type: 'certificate', action: 'Certificate Issued', user: 'admin@iqraskills.org', details: 'CERT-2026-001', timestamp: '2026-04-05 17:00:00' },
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'auth': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'student': return <User className="w-4 h-4 text-emerald-500" />;
      case 'center': return <Settings className="w-4 h-4 text-purple-500" />;
      case 'payment': return <Activity className="w-4 h-4 text-amber-500" />;
      default: return <Activity className="w-4 h-4 text-slate-500" />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'auth': return 'bg-blue-100 text-blue-700';
      case 'student': return 'bg-emerald-100 text-emerald-700';
      case 'center': return 'bg-purple-100 text-purple-700';
      case 'payment': return 'bg-amber-100 text-amber-700';
      case 'course': return 'bg-cyan-100 text-cyan-700';
      case 'certificate': return 'bg-pink-100 text-pink-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.details?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || log.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activity Logs</h1>
          <p className="text-slate-500 mt-1">System activity and audit trail</p>
        </div>
        <Button onClick={fetchLogs} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
        >
          <option value="all">All Types</option>
          <option value="auth">Authentication</option>
          <option value="student">Students</option>
          <option value="center">Centers</option>
          <option value="course">Courses</option>
          <option value="payment">Payments</option>
          <option value="certificate">Certificates</option>
        </select>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Action</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">User</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Details</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No logs found
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(log.type)}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeBadgeColor(log.type)}`}>
                        {typeof log.type === 'string' ? log.type : String(log.type || '')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{typeof log.action === 'string' ? log.action : String(log.action || '')}</td>
                  <td className="px-4 py-3 text-slate-600">{typeof log.user === 'string' ? log.user : (log.user?.name || log.user?.email || String(log.user || ''))}</td>
                  <td className="px-4 py-3 text-slate-500">{typeof log.details === 'string' ? log.details : (typeof log.ip === 'string' ? log.ip : '-')}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="w-3 h-3" />
                      {typeof log.timestamp === 'string' ? log.timestamp : String(log.timestamp || '')}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminLogsPage;
